import { Fragment, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { createPortalApi } from '../api/portalApi';
import { useRuntimeConfig } from '../context/RuntimeConfigContext';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import Modal from '../components/Modal';
import Button from '../components/Button';
import { Icon } from '../components/icons';
import { extractTextFromFile } from '../utils/documentExtractor';
import type { Project, ProjectSource, ContextSummary, ContextVersion } from '../types';

const SOURCE_STATUS_LABEL: Record<string, { label: string; cls: string }> = {
  pending:    { label: 'Queued',     cls: 'badge-review' },
  processing: { label: 'Processing', cls: 'badge-review' },
  ready:      { label: 'Ready',      cls: 'badge-approved' },
  failed:     { label: 'Failed',     cls: 'badge-error' },
};

const IN_FLIGHT_STATUSES = new Set(['pending', 'processing']);

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { instance } = useMsal();
  const cfg = useRuntimeConfig();
  const api = createPortalApi(instance, cfg.msalClientId, cfg.apiBaseUrl);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const confirmDialog = useConfirm();

  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);

  // ── Context summary fields ──────────────────────────────────────────────
  const [domain, setDomain] = useState('');
  const [aboutProduct, setAboutProduct] = useState('');
  const [usersAndUse, setUsersAndUse] = useState('');
  const [currentSummary, setCurrentSummary] = useState<ContextSummary | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [draftSummary, setDraftSummary] = useState<string | null>(null);
  const [draftTokens, setDraftTokens] = useState(0);
  const [confirming, setConfirming] = useState(false);

  // ── Sources ──────────────────────────────────────────────────────────────
  const [sources, setSources] = useState<ProjectSource[]>([]);
  const [addingText, setAddingText] = useState(false);
  const [textDisplayName, setTextDisplayName] = useState('');
  const [textBody, setTextBody] = useState('');
  const [urlValue, setUrlValue] = useState('');
  const [urlCookie, setUrlCookie] = useState('');
  const [showCookieField, setShowCookieField] = useState(false);
  const [sourceBusy, setSourceBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Version history ──────────────────────────────────────────────────────
  const [versions, setVersions] = useState<ContextVersion[]>([]);
  const [expandedVersion, setExpandedVersion] = useState<string | null>(null);

  async function loadAll() {
    if (!id) return;
    try {
      const [p, summary, sourceList, versionList] = await Promise.all([
        api.getProject(id),
        api.getContextSummary(id).catch(() => null),
        api.getProjectSources(id),
        api.getContextVersions(id),
      ]);
      setProject(p);
      setDomain(p.domain ?? '');
      setAboutProduct(prev => prev || p.description || '');
      if (summary && summary.summaryText) setCurrentSummary(summary);
      setSources(sourceList);
      setVersions(versionList);
    } catch (e: any) {
      showToast(e.message ?? 'Failed to load project.', 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Poll while any source is still being processed, so status updates without a manual refresh.
  useEffect(() => {
    if (!sources.some(s => IN_FLIGHT_STATUSES.has(s.status))) return;
    const t = setInterval(async () => {
      if (!id) return;
      try { setSources(await api.getProjectSources(id)); } catch { /* keep last known state */ }
    }, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sources, id]);

  async function handlePreview() {
    if (!id) return;
    if (!domain.trim() && !aboutProduct.trim() && !usersAndUse.trim()) {
      showToast('Fill in at least one of domain, about the product, or users and use.', 'error');
      return;
    }
    setPreviewing(true);
    try {
      const result = await api.previewContextSummary(id, {
        domain: domain.trim() || undefined,
        aboutProduct: aboutProduct.trim() || undefined,
        usersAndUse: usersAndUse.trim() || undefined,
      });
      setDraftSummary(result.draftSummary);
      setDraftTokens(result.estimatedTokens);
    } catch (e: any) {
      showToast(e.message ?? 'Failed to draft a summary.', 'error');
    } finally {
      setPreviewing(false);
    }
  }

  async function handleConfirmSummary() {
    if (!id || draftSummary === null) return;
    setConfirming(true);
    try {
      const saved = await api.confirmContextSummary(id, draftSummary);
      setCurrentSummary(saved);
      setDraftSummary(null);
      showToast('Context summary saved.', 'success');
      setVersions(await api.getContextVersions(id));
    } catch (e: any) {
      showToast(e.message ?? 'Failed to save the summary.', 'error');
    } finally {
      setConfirming(false);
    }
  }

  async function handleAddTextSource() {
    if (!id || !textBody.trim()) return;
    setSourceBusy(true);
    try {
      await api.createTextSource(id, { displayName: textDisplayName.trim() || undefined, text: textBody.trim() });
      setTextDisplayName(''); setTextBody(''); setAddingText(false);
      setSources(await api.getProjectSources(id));
      showToast('Text source added.', 'success');
    } catch (e: any) {
      showToast(e.message ?? 'Failed to add source.', 'error');
    } finally {
      setSourceBusy(false);
    }
  }

  async function handleAddUrlSource() {
    if (!id || !urlValue.trim()) return;
    setSourceBusy(true);
    try {
      await api.createUrlSource(id, { url: urlValue.trim(), cookieHeader: urlCookie.trim() || undefined });
      setUrlValue(''); setUrlCookie(''); setShowCookieField(false);
      setSources(await api.getProjectSources(id));
      showToast('URL queued for ingestion.', 'success');
    } catch (e: any) {
      showToast(e.message ?? 'URL ingestion isn’t available yet.', 'error');
    } finally {
      setSourceBusy(false);
    }
  }

  async function handleFileSelected(file: File) {
    if (!id) return;
    setSourceBusy(true);
    try {
      const text = await extractTextFromFile(file);
      if (!text.trim()) throw new Error('Could not extract any text from this file.');
      await api.createDocumentSource(id, file, text, file.name);
      setSources(await api.getProjectSources(id));
      showToast('Document added.', 'success');
    } catch (e: any) {
      showToast(e.message ?? 'Failed to add document.', 'error');
    } finally {
      setSourceBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleDeleteSource(source: ProjectSource) {
    if (!id) return;
    const ok = await confirmDialog(`Remove "${source.displayName}"? Its chunks will no longer be used for retrieval.`, {
      title: 'Remove source', confirmLabel: 'Remove', danger: true,
    });
    if (!ok) return;
    try {
      await api.deleteProjectSource(id, source.id);
      setSources(await api.getProjectSources(id));
    } catch (e: any) {
      showToast(e.message ?? 'Failed to remove source.', 'error');
    }
  }

  if (loading) return <div className="page-loading"><span className="spinner" />Loading project…</div>;
  if (!project || !id) return <div className="page"><p>Project not found.</p></div>;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <button className="btn-back" onClick={() => navigate('/settings/projects')}><Icon name="arrow-left" size={13} /> Projects</button>
          <h1 className="page-title">{project.name}</h1>
          <p className="page-subtitle">
            {project.domain || 'No domain set'} · Capture what this product is so AI generation can be grounded in it.
          </p>
        </div>
      </div>

      {/* ── Context summary ──────────────────────────────────────────────── */}
      <div className="settings-card">
        <h2 className="settings-section-title">Context summary</h2>
        <p className="settings-section-desc">
          A short, always-on description — domain, what the product does, who uses it — injected
          into every AI generation request for this project. Drafts are AI-generated but never
          saved until you review and confirm them.
        </p>

        <div className="form-grid">
          <div className="form-group">
            <label>Domain</label>
            <input className="form-input" placeholder="e.g. Healthcare, E-commerce, Banking" value={domain} onChange={e => setDomain(e.target.value)} />
          </div>
        </div>
        <div className="form-group mb-sm">
          <label>About the product</label>
          <textarea className="form-input" rows={3} placeholder="What does this product do?" value={aboutProduct} onChange={e => setAboutProduct(e.target.value)} style={{ resize: 'vertical' }} />
        </div>
        <div className="form-group mb-sm">
          <label>Users and use</label>
          <textarea className="form-input" rows={3} placeholder="Who uses it, and how? End customers, internal staff, both?" value={usersAndUse} onChange={e => setUsersAndUse(e.target.value)} style={{ resize: 'vertical' }} />
        </div>

        <Button onClick={handlePreview} disabled={previewing} isGenerating={previewing} variant="ai">
          {previewing ? 'Drafting…' : 'Preview draft summary'}
        </Button>

        {currentSummary && (
          <div className="callout-box" style={{ marginTop: '1rem' }}>
            <div className="row-flex" style={{ justifyContent: 'space-between', marginBottom: '0.4rem' }}>
              <strong style={{ fontSize: 13 }}>Active summary</strong>
              <span className="hint-text">
                {currentSummary.estimatedTokens} tokens
                {currentSummary.updatedAt && ` · updated ${new Date(currentSummary.updatedAt).toLocaleDateString()}`}
                {currentSummary.updatedBy && ` by ${currentSummary.updatedBy}`}
              </span>
            </div>
            <p style={{ margin: 0, fontSize: 13.5, whiteSpace: 'pre-wrap' }}>{currentSummary.summaryText}</p>
          </div>
        )}
        {!currentSummary && (
          <p className="hint-text" style={{ marginTop: '0.75rem' }}>
            No context confirmed yet — generation for this project runs in fallback/generic mode until you save one.
          </p>
        )}
      </div>

      {/* ── Draft preview modal ──────────────────────────────────────────── */}
      {draftSummary !== null && (
        <Modal
          title="Review draft summary"
          onClose={() => setDraftSummary(null)}
          width={560}
          footer={
            <>
              <button className="btn btn-outline" onClick={() => setDraftSummary(null)}>Discard</button>
              <button className="btn btn-primary" disabled={confirming} onClick={handleConfirmSummary}>
                {confirming ? 'Saving…' : 'Confirm & save'}
              </button>
            </>
          }
        >
          <p className="hint-text" style={{ marginTop: 0 }}>
            Edit freely before saving — this becomes the project's active context (~{draftTokens} tokens as drafted).
          </p>
          <textarea
            className="form-input"
            rows={10}
            value={draftSummary}
            onChange={e => setDraftSummary(e.target.value)}
            style={{ resize: 'vertical' }}
          />
        </Modal>
      )}

      {/* ── Sources ──────────────────────────────────────────────────────── */}
      <div className="settings-card">
        <h2 className="settings-section-title">Sources</h2>
        <p className="settings-section-desc">
          Documents, pages, and notes that get chunked and embedded for retrieval — the AI pulls the
          most relevant excerpts into each request instead of the whole document every time.
        </p>

        {sources.length === 0 ? (
          <p style={{ color: 'var(--t2)', padding: '0.5rem 0' }}>No sources added yet.</p>
        ) : (
          <div className="users-table-wrapper">
            <table className="sessions-table">
              <thead><tr><th>Name</th><th>Type</th><th>Status</th><th>Added</th><th /></tr></thead>
              <tbody>
                {sources.map(s => {
                  const st = SOURCE_STATUS_LABEL[s.status] ?? SOURCE_STATUS_LABEL.pending;
                  return (
                    <tr key={s.id}>
                      <td>
                        <div style={{ fontWeight: 500 }}>{s.displayName}</div>
                        {s.status === 'failed' && s.errorMessage && <div className="hint-text">{s.errorMessage}</div>}
                      </td>
                      <td style={{ fontSize: 13, color: 'var(--t2)', textTransform: 'capitalize' }}>{s.sourceType}</td>
                      <td><span className={`badge ${st.cls}`}>{st.label}</span></td>
                      <td style={{ fontSize: 13, color: 'var(--t2)' }}>{new Date(s.ingestedAt).toLocaleDateString()}</td>
                      <td>
                        <button className="btn btn-sm btn-outline" onClick={() => handleDeleteSource(s)}>
                          <Icon name="trash" size={13} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="row-flex" style={{ gap: '0.6rem', flexWrap: 'wrap', marginTop: '1rem' }}>
          <label className={`gen-dropzone ${sourceBusy ? 'has-file' : ''}`} style={{ minHeight: 'auto', padding: '0.6rem 1rem', flex: '1 1 220px' }}>
            <span className="gen-dropzone-text">{sourceBusy ? 'Working…' : 'Upload a document'}</span>
            <span className="gen-dropzone-hint">PDF · Word · Excel · TXT · CSV · MD</span>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.xlsx,.xls,.txt,.csv,.md"
              style={{ display: 'none' }}
              disabled={sourceBusy}
              onChange={e => { const f = e.target.files?.[0]; if (f) handleFileSelected(f); }}
            />
          </label>
          <button className="btn btn-outline btn-sm" onClick={() => setAddingText(v => !v)}>
            <Icon name="plus" size={13} /> Add text
          </button>
        </div>

        {addingText && (
          <div className="callout-box" style={{ marginTop: '0.75rem' }}>
            <div className="form-group">
              <label>Display name</label>
              <input className="form-input" value={textDisplayName} onChange={e => setTextDisplayName(e.target.value)} placeholder="e.g. Support FAQ notes" />
            </div>
            <div className="form-group mb-sm">
              <label>Text</label>
              <textarea className="form-input" rows={4} value={textBody} onChange={e => setTextBody(e.target.value)} style={{ resize: 'vertical' }} />
            </div>
            <button className="btn btn-primary btn-sm" disabled={sourceBusy || !textBody.trim()} onClick={handleAddTextSource}>
              Add
            </button>
          </div>
        )}

        <div className="callout-box" style={{ marginTop: '0.75rem' }}>
          <div className="form-group">
            <label>Add a URL</label>
            <input className="form-input" placeholder="https://…" value={urlValue} onChange={e => setUrlValue(e.target.value)} />
          </div>
          <button className="btn-link" style={{ fontSize: 12, marginBottom: '0.5rem' }} onClick={() => setShowCookieField(v => !v)}>
            {showCookieField ? 'Hide advanced' : 'Advanced: paste a session cookie for login-gated pages'}
          </button>
          {showCookieField && (
            <div className="form-group mb-sm">
              <label>Cookie header</label>
              <input className="form-input" value={urlCookie} onChange={e => setUrlCookie(e.target.value)} placeholder="Pasted for this request only — never stored" />
            </div>
          )}
          <button className="btn btn-primary btn-sm" disabled={sourceBusy || !urlValue.trim()} onClick={handleAddUrlSource}>
            Add URL
          </button>
        </div>
      </div>

      {/* ── Version history ──────────────────────────────────────────────── */}
      <div className="settings-card">
        <h2 className="settings-section-title">Version history</h2>
        {versions.length === 0 ? (
          <p style={{ color: 'var(--t2)', padding: '0.5rem 0' }}>No confirmed versions yet.</p>
        ) : (
          <div className="users-table-wrapper">
            <table className="sessions-table">
              <thead><tr><th>Version</th><th>Status</th><th>Changed by</th><th>Changed at</th><th /></tr></thead>
              <tbody>
                {versions.map(v => (
                  <Fragment key={v.versionId}>
                    <tr>
                      <td className="mono">v{v.versionNumber}</td>
                      <td><span className={`badge ${v.status === 'confirmed' ? 'badge-approved' : 'badge-review'}`}>{v.status}</span></td>
                      <td style={{ fontSize: 13, color: 'var(--t2)' }}>{v.changedBy}</td>
                      <td style={{ fontSize: 13, color: 'var(--t2)' }}>{new Date(v.changedAt).toLocaleString()}</td>
                      <td>
                        <button className="btn btn-sm btn-outline" onClick={() => setExpandedVersion(x => x === v.versionId ? null : v.versionId)}>
                          {expandedVersion === v.versionId ? 'Hide' : 'View'}
                        </button>
                      </td>
                    </tr>
                    {expandedVersion === v.versionId && (
                      <tr>
                        <td colSpan={5}>
                          <p style={{ margin: 0, fontSize: 13, whiteSpace: 'pre-wrap', color: 'var(--t2)' }}>{v.summaryTextSnapshot}</p>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
